import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {
  getApiCredits,
  getApiInvoices,
  postApiCreditsResend,
  postApiInvoicesResend,
  putApiCredits,
  putApiInvoices,
} from '@/api/api.generated';
import type {CreditDocument, InvoiceDocument} from '@/api/types/api.types';
import {DocumentStatus} from './types';
import {
  toCreditTransaction,
  toCreditTransactions,
  toTransaction,
  toTransactions,
} from './adapters';
import type {
  Paged,
  Transaction,
  TransactionQuery,
  TransactionType,
} from './types';

type GetInvoicesParams = NonNullable<Parameters<typeof getApiInvoices>[0]>;
type GetCreditsParams = NonNullable<Parameters<typeof getApiCredits>[0]>;
type FilterStatus = NonNullable<GetInvoicesParams['status']>;

// DocumentStatus values mirror the backend vocabulary 1:1 — pass through.
function toApiStatus(s: DocumentStatus | undefined): FilterStatus | undefined {
  return (s as FilterStatus | undefined) ?? undefined;
}

/**
 * Compose the backend's composite document id, `{number}#{location}#{groupDb}`.
 * Returns undefined unless all three segments are present — the key can't be
 * looked up partially, so there is no meaningful prefix to send.
 */
function toDocumentId(q: TransactionQuery): string | undefined {
  const number = q.number?.trim();
  const location = q.location?.trim();
  const groupDb = q.groupDb?.trim();
  if (!number || !location || !groupDb) return undefined;
  return `${number}#${location}#${groupDb}`;
}

/** Whether a 'name' mode query has enough to form a composite id. */
export function canLookUpByNumber(q: TransactionQuery): boolean {
  return toDocumentId(q) !== undefined;
}

/**
 * The contract is `{data: [...], pagination}` for every query, by-id included.
 * This stays defensive because a pre-cde9feb backend build returned the by-id
 * document as a bare object, which reaches `toTransactions` and throws on
 * `.map`. Tolerating both shapes means a stale deploy degrades to a working
 * lookup rather than an error page.
 */
function toList<T>(data: T | T[] | undefined | null): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
}

/** Filter params shared by both endpoints. */
function toFilterParams(q: TransactionQuery): GetCreditsParams {
  return {
    status: toApiStatus(q.status as DocumentStatus | undefined),
    provider: q.partnerId,
    location: q.location || undefined,
    createdFrom: q.dateFrom,
    createdTo: q.dateTo,
    limit: String(q.limit ?? 25),
    exclusiveStartKey: q.cursor,
  };
}

function toInvoiceParams(q: TransactionQuery): GetInvoicesParams {
  // A by-id lookup and a filtered list are different access patterns on the
  // backend, so `id` travels alone — pairing it with status/provider/dates
  // would be ambiguous about which query was intended.
  if (q.mode === 'name') return {id: toDocumentId(q)};
  return toFilterParams(q);
}

function toCreditParams(q: TransactionQuery): GetCreditsParams {
  if (q.mode === 'name') return {id: toDocumentId(q)};
  return toFilterParams(q);
}

export function useTransactions(query: TransactionQuery = {mode: 'status'}) {
  const isNameLookup = query.mode === 'name';
  return useQuery({
    queryKey: ['transactions', query],
    // Don't fire a name lookup until the full composite id is available.
    enabled: !isNameLookup || canLookUpByNumber(query),
    queryFn: async (): Promise<Paged<Transaction>> => {
      const res =
        query.type === 'credit'
          ? await getApiCredits(toCreditParams(query))
          : await getApiInvoices(toInvoiceParams(query));
      let items =
        query.type === 'credit'
          ? toCreditTransactions(
              toList((res as Awaited<ReturnType<typeof getApiCredits>>).data)
            )
          : toTransactions(
              toList((res as Awaited<ReturnType<typeof getApiInvoices>>).data)
            );

      // A name lookup is already pinned to number/location/group by the id, so
      // narrowing it again here could only wrongly drop the single exact match.
      if (!isNameLookup) {
        // Client-side filters the backend doesn't support. `location` and
        // `status` are handled server-side via toApiParams; the rest aren't
        // queryable, so they're narrowed against the returned page. Note this
        // only sees the current page — a match further along is missed.
        if (query.groupDb)
          items = items.filter(t => t.groupDb === query.groupDb);
        if (query.direction)
          items = items.filter(t => t.direction === query.direction);
        if (query.type) items = items.filter(t => t.type === query.type);
        if (query.docType)
          items = items.filter(t => t.docType === query.docType);
      }

      return {
        items,
        nextCursor: res.pagination?.lastEvaluatedKey ?? undefined,
        hasMore: Boolean(res.pagination?.hasMore),
      };
    },
  });
}

/**
 * Single-transaction fetch by composite id (`{number}#{location}#{groupDb}`),
 * served by each endpoint's `id` parameter.
 */
export function useTransaction(
  id: string | null,
  type: TransactionType = 'invoice'
) {
  return useQuery({
    queryKey: ['transactions', type, id],
    queryFn: async (): Promise<Transaction | null> => {
      if (!id) return null;
      if (type === 'credit') {
        const res = await getApiCredits({id});
        const doc = toList(res.data)[0];
        return doc ? toCreditTransaction(doc) : null;
      }
      const res = await getApiInvoices({id});
      const doc = toList(res.data)[0];
      return doc ? toTransaction(doc) : null;
    },
    enabled: !!id,
  });
}

/**
 * Update an invoice via PUT /invoices.
 *
 * The backend currently requires the full `invoiceData` blob on every update,
 * even for status-only changes. Callers pass the changes they want; the hook
 * handles invalidating the transaction caches on success.
 */
export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: InvoiceDocument) => putApiInvoices(body),
    onSuccess: () => {
      qc.invalidateQueries({queryKey: ['transactions']});
    },
  });
}

export function useUpdateCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreditDocument) => putApiCredits(body),
    onSuccess: () => {
      qc.invalidateQueries({queryKey: ['transactions']});
    },
  });
}

/**
 * Resend an invoice or credit document back to Tungsten.
 * The endpoint pair is split by document type — pick the right one based on
 * the view-model `type` so credits don't hit the invoice endpoint.
 */
export function useResendTransaction() {
  return useMutation({
    mutationFn: async ({id, type}: {id: string; type: 'invoice' | 'credit'}) =>
      type === 'credit'
        ? postApiCreditsResend({id})
        : postApiInvoicesResend({id}),
  });
}
